import { Change, ChangeSet, simplifyChanges } from 'prosemirror-changeset'
import { Node } from 'prosemirror-model'
import { StepMap } from 'prosemirror-transform'

import type { RemovedPositionDetails } from '#choo-app/lib/editor/plugins/revision-asterisks/helpers'

export interface Comparison {
  identical: boolean
  changesRelativeToRight(): {
    changed: [number, number][]
    removed: RemovedPositionDetails[]
  }
}

export class NodeDiff implements Comparison {
  private readonly left: Node
  private readonly right: Node

  constructor({ left, right }: { left: Node; right: Node }) {
    this.left = left
    this.right = right
  }

  /**
   * The prosemirror-changeset object between the left and right docs
   */
  private get changeSet(): ChangeSet {
    const changeSet = ChangeSet.create(this.left)
    const leftSize = this.left.content.size
    const rightSize = this.right.content.size
    const stepMap = new StepMap([0, leftSize, rightSize])
    return changeSet.addSteps(this.right, [stepMap], null)
  }

  get changes(): Change[] {
    return simplifyChanges(this.changeSet.changes, this.right)
  }

  get identical(): boolean {
    return this.changes.length === 0
  }

  changesRelativeToRight() {
    const changed: [number, number][] = []
    const removed: RemovedPositionDetails[] = []
    const seenPositions = new Set<number>()

    for (const change of this.changes) {
      if (change.toB > change.fromB) {
        changed.push([change.fromB, change.toB])
      }

      if (change.toA > change.fromA && !seenPositions.has(change.fromB)) {
        seenPositions.add(change.fromB)
        removed.push({
          position: change.fromB,
          text: this.left.content.textBetween(change.fromA, change.toA, ' '),
        })
      }
    }

    return { changed, removed }
  }
}
